/**
 * Money: integer minor units + ISO 4217 currency. Unlike currencies are never added together;
 * totals are always grouped by currency. Splits reconcile exactly with a stable remainder rule.
 */

export interface Money { minor: number; currency: string }

export function currencyDigits(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export function formatMoney(minor: number, currency: string, locale = 'en-GB'): string {
  const digits = currencyDigits(currency);
  return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits })
    .format(minor / 10 ** digits).replace(/[ \u202f]/g, '\u00a0'); // one value, one line
}

export type ParseResult = { ok: true; minor: number } | { ok: false; reason: 'empty' | 'invalid' | 'ambiguous' | 'negative' | 'too_precise' };

/**
 * Parse a typed amount. Accepts "12", "12.5", "12.50", "1,234.50", "1.234,50", "12,50".
 * A lone separator followed by exactly three digits ("1,234" / "1.234") is ambiguous for
 * currencies with decimals, so the user is asked rather than guessed for.
 */
export function parseAmount(input: string, currency: string): ParseResult {
  const s = input.trim().replace(/[\s  ']/g, '').replace(/^[^\d\-.,]+|[^\d.,]+$/g, '');
  if (!s) return { ok: false, reason: 'empty' };
  if (s.startsWith('-')) return { ok: false, reason: 'negative' };
  if (!/^[\d.,]+$/.test(s)) return { ok: false, reason: 'invalid' };
  const digits = currencyDigits(currency);
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let intPart: string; let frac = '';
  if (lastDot === -1 && lastComma === -1) {
    intPart = s;
  } else {
    const sep = lastDot > lastComma ? '.' : ',';
    const other = sep === '.' ? ',' : '.';
    const idx = Math.max(lastDot, lastComma);
    const after = s.slice(idx + 1);
    const occurrences = s.split(sep).length - 1;
    const hasOther = s.includes(other);
    if (!hasOther && occurrences === 1 && after.length === 3 && digits > 0) return { ok: false, reason: 'ambiguous' };
    if (!hasOther && (occurrences > 1 || (digits === 0 && after.length === 3))) {
      // "1.234.567" — grouping only
      if (!s.split(sep).slice(1).every((g) => g.length === 3)) return { ok: false, reason: 'invalid' };
      intPart = s.split(sep).join('');
    } else {
      intPart = s.slice(0, idx).split(other).join('');
      if (hasOther && !s.slice(0, idx).split(other).slice(1).every((g) => g.length === 3)) return { ok: false, reason: 'invalid' };
      frac = after;
    }
  }
  if (!/^\d+$/.test(intPart || '0') || !/^\d*$/.test(frac)) return { ok: false, reason: 'invalid' };
  if (frac.length > digits) return { ok: false, reason: 'too_precise' };
  const minor = Number(intPart || '0') * 10 ** digits + Number(frac.padEnd(digits, '0') || '0');
  if (!Number.isSafeInteger(minor)) return { ok: false, reason: 'invalid' };
  return { ok: true, minor };
}

/** Equal split with the remainder handed out one minor unit at a time in stable id order. */
export function splitEqual(total: number, ids: string[]): Map<string, number> {
  const sorted = [...new Set(ids)].sort();
  const out = new Map<string, number>();
  if (sorted.length === 0) return out;
  const base = Math.floor(total / sorted.length);
  let rem = total - base * sorted.length;
  for (const id of sorted) {
    out.set(id, base + (rem > 0 ? 1 : 0));
    if (rem > 0) rem--;
  }
  return out;
}

/** Percentage split; percentages must total exactly 100. Same remainder rule as the database. */
export function splitPercent(total: number, pct: Record<string, number>): Map<string, number> {
  const ids = Object.keys(pct).sort();
  const sum = ids.reduce((s, id) => s + pct[id]!, 0);
  if (Math.abs(sum - 100) > 1e-9) throw new Error('Percentages must total 100');
  const out = new Map<string, number>();
  let assigned = 0;
  for (const id of ids) {
    const v = Math.floor((total * pct[id]!) / 100);
    out.set(id, v);
    assigned += v;
  }
  let rem = total - assigned;
  for (const id of ids) {
    if (rem <= 0) break;
    out.set(id, out.get(id)! + 1);
    rem--;
  }
  return out;
}

export function sumByCurrency<T>(items: T[], pick: (t: T) => Money | null): Map<string, number> {
  const out = new Map<string, number>();
  for (const it of items) {
    const m = pick(it);
    if (!m) continue;
    out.set(m.currency, (out.get(m.currency) ?? 0) + m.minor);
  }
  return out;
}

export interface BudgetLine {
  currency: string;
  expected_total_minor: number | null;
  low_total_minor: number | null;
  high_total_minor: number | null;
  mandatory: boolean;
}

export interface BudgetSummary {
  currency: string;
  expected: number;
  low: number;
  high: number;
  unpriced: number;
  optionalExpected: number;
}

/**
 * Planned-cost summary per currency. Low/high fall back to the expected value when a line has
 * no range; unpriced lines are counted, never treated as zero cost silently.
 */
export function summarizeBudget(lines: BudgetLine[]): BudgetSummary[] {
  const by = new Map<string, BudgetSummary>();
  for (const l of lines) {
    const s = by.get(l.currency) ?? { currency: l.currency, expected: 0, low: 0, high: 0, unpriced: 0, optionalExpected: 0 };
    if (l.expected_total_minor === null) {
      s.unpriced++;
    } else if (!l.mandatory) {
      s.optionalExpected += l.expected_total_minor;
    } else {
      s.expected += l.expected_total_minor;
      s.low += l.low_total_minor ?? l.expected_total_minor;
      s.high += l.high_total_minor ?? l.expected_total_minor;
    }
    by.set(l.currency, s);
  }
  return [...by.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

export interface LedgerExpense { id: string; payer_id: string; amount_minor: number; currency: string; status: string }
export interface LedgerShare { expense_id: string; user_id: string; share_minor: number }
export interface LedgerSettlement { from_user: string; to_user: string; amount_minor: number; currency: string; status: string }

/**
 * Net balance per person per currency from agreed shares (not planning estimates).
 * Positive = others owe this person. Only confirmed settlements change balances; reimbursements
 * move who owes whom and never add to trip cost.
 */
export function balances(expenses: LedgerExpense[], shares: LedgerShare[], settlements: LedgerSettlement[]): Map<string, Map<string, number>> {
  const out = new Map<string, Map<string, number>>();
  const add = (cur: string, user: string, v: number) => {
    const m = out.get(cur) ?? new Map<string, number>();
    m.set(user, (m.get(user) ?? 0) + v);
    out.set(cur, m);
  };
  const live = new Map(expenses.filter((e) => e.status === 'confirmed').map((e) => [e.id, e]));
  for (const e of live.values()) add(e.currency, e.payer_id, e.amount_minor);
  for (const s of shares) {
    const e = live.get(s.expense_id);
    if (e) add(e.currency, s.user_id, -s.share_minor);
  }
  for (const st of settlements) {
    if (st.status !== 'confirmed') continue;
    add(st.currency, st.from_user, st.amount_minor);
    add(st.currency, st.to_user, -st.amount_minor);
  }
  return out;
}

export const UNIT_LABELS: Record<string, string> = {
  flat: 'flat amount', per_person: 'per person', per_adult: 'per adult', per_child: 'per child', per_room: 'per room',
  per_night: 'per night', per_room_night: 'per room-night', per_vehicle: 'per vehicle', per_seat: 'per seat',
  per_booking: 'per booking', per_activity: 'per activity', per_group: 'per group', per_day: 'per day',
};

export const PRICE_KIND_LABELS: Record<string, string> = {
  rough_benchmark: 'Rough benchmark', sourced_estimate: 'Sourced estimate', live_quote: 'Live quote',
  user_quote: 'Your quote', confirmed_booking: 'Confirmed booking cost',
};

/** Minor units → plain editable string ("1234.5" style, no grouping) for form fields. */
export function minorToInput(minor: number | null | undefined, currency: string): string {
  if (minor == null) return '';
  const d = currencyDigits(currency);
  return d === 0 ? String(minor) : (minor / 10 ** d).toFixed(d);
}
